import { useState } from 'react'
import { Avatar, Mentions, message, Popconfirm, Space, Typography as TypographyAntd } from 'antd'
import Button from 'modules/core/components/Button/Button'
import ButtonActionBar from 'modules/core/components/ButtonActionBar/ButtonActionBar'
import DropdownMenu from 'modules/core/components/DropdownMenu/DropdownMenu'
import Typography from 'modules/core/components/Typography/Typography'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  UpOutlined,
  DownOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import configuration from 'modules/configuration'
import PropTypes from 'prop-types'

import './CardData.scss'
import { generateMessageError } from 'modules/core/utils'

const { Paragraph } = TypographyAntd

const CardData = ({
  el,
  editId,
  onClickEdit,
  onClickReplies = () => {},
  isParent = false,
  usersList,
  onRefetchComments,
  onCleanHasComment,
  setElementRefId,
  deleteComment,
  editComment,
  resolveComment,
  setHasComment,
}) => {
  const [deleteId, setDeleteId] = useState(null)
  const [usersIds, setUsersIds] = useState([])
  const [textValue, setTextValue] = useState('')
  const { t } = useTranslation()

  const handleDelete = () => {
    deleteComment(deleteId)
      .then(() => {
        onRefetchComments().then(() => {
          message.success(t('FEEDBACK_SAVE_CHANGES_SUCCES'), 8)
          setDeleteId(null)
          onCleanHasComment()
        })
      })
      .catch((error) => {
        message.error(generateMessageError(error, 'FEEDBACK_SAVE_CHANGES_FAIL_MESSAGE'), 8)
        setDeleteId(null)
        onCleanHasComment()
      })
  }

  const handleEdit = () => {
    const { key, parent, module } = el
    editComment(editId, { key, parent, module, text: textValue, mentions: usersIds })
      .then(() => {
        onRefetchComments().then(() => {
          message.success(t('FEEDBACK_SAVE_CHANGES_SUCCES'), 8)
          onClickEdit(null)
        })
      })
      .catch((error) => {
        message.error(generateMessageError(error, 'FEEDBACK_SAVE_CHANGES_FAIL_MESSAGE'), 8)
        onClickEdit(null)
      })
  }

  const handleResolved = (id) => {
    resolveComment(id)
      .then(() =>
        onRefetchComments().then(() => {
          message.success(t('FEEDBACK_SAVE_CHANGES_SUCCES'), 8)
        }),
      )
      .catch((error) => {
        message.error(generateMessageError(error, 'FEEDBACK_SAVE_CHANGES_FAIL_MESSAGE'), 8)
      })
  }

  const renderText = () => {
    const aux = el.text.split(/@(\w+)/)
    return aux.map((part, index) =>
      index % 2 === 1 ? (
        <span style={{ color: '#0047ba' }} key={`${part}_${index}`}>
          @{part}
        </span>
      ) : (
        part
      ),
    )
  }

  const handleOnClick = (key, module) => {
    const identifyData = key.split(module)[1]
    const type = identifyData.split('-')[1]
    const id = identifyData.split('-')[2]
    const data = {
      type,
      data: true,
      ...(type !== 'table' && {
        key: module,
        id,
      }),
    }
    setHasComment(data)
  }

  const handleSelect = (id) => {
    id = parseInt(id)
    // Agregar el ID seleccionado al estado si no está ya presente
    setUsersIds((prevIds) => {
      if (!prevIds.includes(id)) {
        return [...prevIds, id]
      }
      return prevIds
    })
  }

  const handleChange = (newValue) => {
    setTextValue(newValue)

    const validIds = usersList?.values
      .filter((user) => newValue.includes(`@${user.first_name}${user.last_name}`))
      .map((user) => user.id)

    setUsersIds(validIds)
  }

  return (
    <div id={el.key} key={el.id}>
      <Space
        onClick={() => handleOnClick(el.key, el.module)}
        style={{ width: '100%', cursor: 'pointer' }}
        direction="vertical"
      >
        <ButtonActionBar position="space-between">
          <Space direction="vertical">
            <Space direction="horizontal">
              <Avatar style={{ backgroundColor: '#0047ba' }} size="small">
                {el?.user?.first_name[0].toUpperCase()}
              </Avatar>
              <Typography.Body level={2}>{el?.user?.first_name}</Typography.Body>
              <Typography.Body level={3}>{moment(el.created_at).calendar()}</Typography.Body>
            </Space>
          </Space>
          <Space direction="horizontal">
            {isParent && !el.resolved && (
              <Button.Icon
                title={t('ACTION_SOLVE')}
                icon={<Typography.Icon level={2} icon={CheckCircleOutlined} />}
                onClick={(e) => {
                  e.stopPropagation()
                  handleResolved(el.id)
                }}
              />
            )}
            <Popconfirm
              title={t('POPCONFIRM_REMOVE_COMMENT')}
              okText={t('ACTION_DELETE')}
              onConfirm={handleDelete}
              cancelText={t('ACTION_CANCEL')}
              onCancel={() => setDeleteId(null)}
              visible={el.id === deleteId}
            >
              <DropdownMenu
                title={t('ACTION_MORE')}
                menu={[
                  ...(!el.resolved
                    ? [
                        {
                          title: t('ACTION_EDIT'),
                          icon: <EditOutlined />,
                          onClick: (e) => {
                            e.domEvent.stopPropagation()
                            onClickEdit(el.id)
                          },
                        },
                      ]
                    : []),
                  {
                    title: t('ACTION_DELETE'),
                    icon: <DeleteOutlined />,
                    onClick: (e) => {
                      e.domEvent.stopPropagation()
                      setDeleteId(el.id)
                    },
                  },
                ]}
              />
            </Popconfirm>
          </Space>
        </ButtonActionBar>
        {editId === el.id ? (
          <Space direction="horizontal">
            <Mentions
              placeholder={t('WRITE_COMMENT_PLACEHOLDER')}
              rows={2}
              defaultValue={el.text}
              onSelect={(d) => handleSelect(d.key)}
              onChange={handleChange}
              onClick={(e) => e.stopPropagation()}
            >
              {usersList?.values.map((user) => (
                <Mentions.Option key={user.id} value={`${user.first_name}${user.last_name}`}>
                  {user.first_name}
                  {user.last_name}
                </Mentions.Option>
              ))}
            </Mentions>
            <Button.Icon
              icon={<Typography.Icon level={2} icon={CloseOutlined} />}
              title="ACTION_CANCEL"
              onClick={(e) => {
                e.stopPropagation()
                onClickEdit(null)
              }}
            />
            {textValue && (
              <Button.Icon
                icon={<Typography.Icon level={2} icon={CheckOutlined} />}
                title="ACTION_SAVE"
                onClick={(e) => {
                  e.stopPropagation()
                  handleEdit()
                }}
              />
            )}
          </Space>
        ) : (
          <Paragraph
            ellipsis={{
              rows: 3,
              expandable: true,
              symbol: t('ACTION_READ_MORE'),
            }}
            key={el.key}
          >
            {renderText()}
          </Paragraph>
        )}
      </Space>
      {isParent && (
        <ButtonActionBar position={!el.resolved ? 'space-between' : 'end'}>
          {!el.resolved && (
            <Button.Link
              title="ACTION_REPLY"
              onClick={(e) => {
                e.stopPropagation()
                onClickReplies()
              }}
            />
          )}
          <Space
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation()
              onClickReplies()
            }}
            direction="horizontal"
          >
            <span>{`#${el.replies.length}`}</span>
            {el.replies.length > 0 && (
              <Typography.Icon level={2} icon={el.viewReplies ? UpOutlined : DownOutlined} />
            )}
          </Space>
        </ButtonActionBar>
      )}
    </div>
  )
}

const mapDispatchToProps = {
  setElementRefId: configuration.actions.setElementRefId,
  deleteComment: configuration.actions.deleteComment,
  editComment: configuration.actions.editComment,
  resolveComment: configuration.actions.resolveComment,
  setHasComment: configuration.actions.setHasComment,
}

CardData.propTypes = {
  el: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent: PropTypes.any,
    module: PropTypes.any,
    text: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user: PropTypes.shape({
      first_name: PropTypes.string,
    }),
    created_at: PropTypes.string,
    resolved: PropTypes.any,
    replies: PropTypes.any,
    viewReplies: PropTypes.any,
  }),
  editId: PropTypes.any,
  onClickEdit: PropTypes.func,
  onClickReplies: PropTypes.func,
  isParent: PropTypes.bool,
  usersList: PropTypes.shape({
    values: PropTypes.array,
  }),
  onRefetchComments: PropTypes.func,
  onCleanHasComment: PropTypes.func,
  setElementRefId: PropTypes.func,
  deleteComment: PropTypes.func,
  editComment: PropTypes.func,
  resolveComment: PropTypes.func,
  setHasComment: PropTypes.func,
}

export default connect(null, mapDispatchToProps)(CardData)
